import { useEffect } from 'react';
import { Form, FormikProvider, useFormik, useFormikContext } from 'formik';
import { toFormikValidate, toFormikValidationSchema } from 'zod-formik-adapter';
import { Label } from '@antimatterhq/ui/components/label';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Text } from '@antimatterhq/ui/components/typography';
import { ButtonGroup } from '@antimatterhq/ui/components/buttonGroup';
import { Button } from '@antimatterhq/ui/components/button';
import { JiraAlerts } from './alerts/JiraAlerts';
import { ServiceNowAlerts } from './alerts/ServiceNowAlerts';
import {
  useGetWorkspaceConfig,
  useOptimisticUpdateWorkspaceConfig,
} from '@/hooks/workspace/useGetWorkspaceConfig';
import { ExportDestinationDropdown } from '@/components/dropdown';
import {
  WorkspaceConfigAlertsSchema,
  WorkspaceConfigAlertsSchemaValues,
} from '@/zod/schemas/workspaceConfig/AlertSchema';
import { useUpdateWorkspaceConfig } from '@/hooks/workspace/useUpdateWorkspaceConfig';
import {
  getExportAlertFormValues,
  setExportAlertFormValues,
} from '@/lib/workspaceConfig/exportAlert';
import { FormError, FormInput, FormTextarea } from '@/components/form/form';
import { ProcessingScheduleRadioGroup } from '@/components/radio';

type AlertProps = {
  formPrefix: 'notables' | 'operational';
  disabled: boolean;
};

const Alert = ({ formPrefix, disabled }: AlertProps) => {
  const {
    values: baseValues,
    handleBlur,
    handleChange,
  } = useFormikContext<WorkspaceConfigAlertsSchemaValues>();
  const values =
    formPrefix === 'notables' ? baseValues.notables : baseValues.operational;

  return (
    <>
      <Label htmlFor="destination" id="destination-label">
        Destination
      </Label>
      <ExportDestinationDropdown
        value={values.destination}
        onBlur={handleBlur}
        onChange={handleChange}
        name={`${formPrefix}.destination`}
        disabled={disabled}
        aria-labelledby="destination-label"
      />

      <Spacer size="base" />

      {values.destination === 'slack' && (
        <>
          <Text size="sm">Notables to Slack Config</Text>

          <Spacer size="base" />

          <ButtonGroup
            name={`${formPrefix}.slackOAuthType`}
            value={values.slackOAuthType}
            options={[
              { label: 'Token', value: 'token' },
              { label: 'Token from Secret', value: 'secret' },
            ]}
            onChange={(value) =>
              handleChange({
                target: { name: `${formPrefix}.slackOAuthType`, value },
              })
            }
            disabled={disabled}
          />

          <Spacer size="base" />

          {values.slackOAuthType === 'token' && (
            <>
              <Label
                htmlFor={`${formPrefix}.slackTokenValue`}
                id={`${formPrefix}.slackTokenValue-label`}
                required
              >
                OAuth Token
              </Label>
              <FormInput
                aria-labelledby={`${formPrefix}.slackTokenValue-label`}
                type="text"
                name={`${formPrefix}.slackTokenValue`}
                value={values.slackTokenValue}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter the OAuth token"
                disabled={disabled}
              />
              <FormError name={`${formPrefix}.slackTokenValue`} />
            </>
          )}

          {values.slackOAuthType === 'secret' && (
            <>
              <Label
                htmlFor={`${formPrefix}.slackTokenKey`}
                id={`${formPrefix}.slackTokenKey-label`}
                required
              >
                Databricks Secret Key
              </Label>
              <FormInput
                aria-labelledby={`${formPrefix}.slackTokenKey-label`}
                type="text"
                name={`${formPrefix}.slackTokenKey`}
                value={values.slackTokenKey}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter the Secret Key"
                disabled={disabled}
              />
              <FormError name={`${formPrefix}.slackTokenKey`} />

              <Spacer size="base" />

              <Label
                htmlFor={`${formPrefix}slackTokenScope`}
                id={`${formPrefix}.slackTokenScope-label`}
                required
              >
                Databricks Secret Scope
              </Label>
              <FormInput
                aria-labelledby={`${formPrefix}.slackTokenScope-label`}
                type="text"
                name={`${formPrefix}.slackTokenScope`}
                value={values.slackTokenScope}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter the Secret Scope"
                disabled={disabled}
              />
              <FormError name={`${formPrefix}.slackTokenScope`} />
            </>
          )}

          <Spacer size="base" />

          <Label htmlFor={`${formPrefix}slackChannel`} required>
            Channel Name
          </Label>
          <FormInput
            type="text"
            id={`${formPrefix}.slackChannel`}
            name={`${formPrefix}.slackChannel`}
            value={values.slackChannel}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter the Slack channel name"
            disabled={disabled}
          />
          <FormError name={`${formPrefix}.slackChannel`} />

          <Spacer size="base" />

          <Label
            htmlFor={`${formPrefix}.slackMessage`}
            id={`${formPrefix}.slackMessage-label`}
            required
          >
            Message
          </Label>
          <FormTextarea
            aria-labelledby={`${formPrefix}.slackMessage-label`}
            id={`${formPrefix}.slackMessage`}
            name={`${formPrefix}.slackMessage`}
            value={values.slackMessage}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter the message that will appear in Slack"
            disabled={disabled}
          />
          <FormError name={`${formPrefix}.slackMessage`} />
        </>
      )}

      {values.destination === 'jira' && (
        <>
          <JiraAlerts formPrefix={formPrefix} disabled={disabled} />
        </>
      )}

      {values.destination === 'servicenow' && (
        <>
          <ServiceNowAlerts formPrefix={formPrefix} disabled={disabled} />
        </>
      )}

      {values.destination === 'webhook' && (
        <>
          <Text size="sm">Webhook target</Text>

          <Spacer size="base" />

          <ButtonGroup
            name={`${formPrefix}.webhookOutputUrlType`}
            value={values.webhookOutputUrlType}
            options={[
              { label: 'Webhook URL', value: 'url' },
              { label: 'URL from Secret', value: 'secret' },
            ]}
            onChange={(value) =>
              handleChange({
                target: { name: `${formPrefix}.webhookOutputUrlType`, value },
              })
            }
            disabled={disabled}
          />

          <Spacer size="base" />

          {values.webhookOutputUrlType === 'url' && (
            <>
              <Label
                htmlFor={`${formPrefix}.webhookUrl`}
                id={`${formPrefix}.webhookUrl-label`}
                required
              >
                Output URL
              </Label>
              <FormInput
                aria-labelledby={`${formPrefix}.webhookUrl-label`}
                type="text"
                name={`${formPrefix}.webhookUrl`}
                value={values.webhookUrl}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter the output URL"
                disabled={disabled}
              />
              <FormError name={`${formPrefix}.webhookUrl`} />
            </>
          )}

          {values.webhookOutputUrlType === 'secret' && (
            <>
              <Label
                htmlFor={`${formPrefix}.webhookKey`}
                id={`${formPrefix}.webhookKey-label`}
                required
              >
                Databricks Secret Key
              </Label>
              <FormInput
                type="text"
                name={`${formPrefix}.webhookKey`}
                value={values.webhookKey}
                aria-labelledby={`${formPrefix}.webhookKey-label`}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter the Secret Key"
                disabled={disabled}
              />
              <FormError name={`${formPrefix}.webhookKey`} />

              <Spacer size="base" />

              <Label
                htmlFor={`${formPrefix}.webhookScope`}
                id={`${formPrefix}.webhookScope-label`}
                required
              >
                Databricks Secret Scope
              </Label>
              <FormInput
                type="text"
                name={`${formPrefix}.webhookScope`}
                value={values.webhookScope}
                aria-labelledby={`${formPrefix}.webhookScope-label`}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter the Secret Scope"
                disabled={disabled}
              />
              <FormError name={`${formPrefix}.webhookScope`} />
            </>
          )}
        </>
      )}
    </>
  );
};

export default function Alerts() {
  const { data } = useGetWorkspaceConfig();
  const { mutateAsync: updateConfig, isPending: isUpdating } =
    useUpdateWorkspaceConfig();
  const optimisticUpdate = useOptimisticUpdateWorkspaceConfig();

  const formik = useFormik({
    initialValues: getExportAlertFormValues(data),
    validationSchema: toFormikValidationSchema(WorkspaceConfigAlertsSchema),
    validate: toFormikValidate(WorkspaceConfigAlertsSchema),
    onSubmit: async (values) => {
      if (!data) {
        return;
      }

      const config = setExportAlertFormValues(data, values);
      // save to API
      await updateConfig(config, {
        onSuccess: async () => {
          optimisticUpdate(config);
        },
        toasts: {
          success: 'Notable & operational alert export configuration updated',
          error:
            'Failed to update notable & operational alert export configuration',
        },
      });
    },
  });

  const { setValues, handleChange, handleBlur, values, errors, touched } =
    formik;

  useEffect(() => {
    if (data) {
      setValues(getExportAlertFormValues(data));
    }
  }, [data, setValues]);

  return (
    <FormikProvider value={formik}>
      <Form>
        <Spacer size="lg" />
        <Text size="base" bold>
          Notable export destination
        </Text>
        <Spacer size="base" />
        <Alert formPrefix="notables" disabled={isUpdating} />

        <Spacer size="lg" />

        <Text size="base" bold>
          Operational alert export destination
        </Text>

        <Spacer size="base" />
        <Alert formPrefix="operational" disabled={isUpdating} />

        <Spacer size="2xl" />

        {(values.notables.destination !== 'none' ||
          values.operational.destination !== 'none') && (
          <>
            <Text size="base" bold>
              Export schedule
            </Text>

            <Spacer size="base" />
            <ProcessingScheduleRadioGroup
              disabled={isUpdating}
              handleChange={handleChange}
              values={values}
              errors={errors}
              touched={touched}
              handleBlur={handleBlur}
              data-testid="processing-schedule-radio-group"
            />
          </>
        )}

        <div className="flex justify-end">
          <Button variant="primary" disabled={isUpdating} type="submit">
            Submit changes
          </Button>
        </div>
      </Form>
    </FormikProvider>
  );
}
